import { useEffect, useRef, useState } from 'react';

import { IconChevronRight, IconChevronLeft } from '@veneer/core';
import { useSwipeable } from 'react-swipeable';

import PlanCard from './plan-card';
import {
  CardsWrapper,
  Container,
  ChevronWrapper,
  LeftFiller,
  RightFiller,
  Title,
  TitleContainer,
} from './styles';

export type CardType = {
  text: string;
  color: string;
  title: string;
  icon: string;
  name?: string;
  pageName?: string;
};

type HowYourPlanWorksType = {
  headerTitle: string;
  cards: CardType[];
  pageName?: string;
  sectionId?: string;
};

function HowYourPlanWorks(props: HowYourPlanWorksType) {
  const { cards, headerTitle, sectionId } = props;
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const cardsWrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const checkButtonsState = () => {
      if (cardsWrapperRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          cardsWrapperRef.current;
        setIsLeftDisabled(scrollLeft === 0);
        setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth);
      }
    };

    checkButtonsState();
    window.addEventListener('resize', checkButtonsState);
    if (cardsWrapperRef.current) {
      cardsWrapperRef.current.addEventListener('scroll', checkButtonsState);
    }
    return () => {
      window.removeEventListener('resize', checkButtonsState);
      if (cardsWrapperRef.current) {
        cardsWrapperRef.current.removeEventListener(
          'scroll',
          checkButtonsState
        );
      }
    };
  }, [cardWidth]);

  const handlers = useSwipeable({
    onSwipedLeft: () => swipeRight(),
    onSwipedRight: () => swipeLeft(),
  });

  const swipeLeft = () => {
    if (cardsWrapperRef.current) {
      const previousCardPosition =
        cardWidth <= 0
          ? cardWidth
          : Math.floor(cardsWrapperRef.current.scrollLeft / cardWidth) *
            cardWidth;
      cardsWrapperRef.current.scrollBy({
        left: previousCardPosition - cardsWrapperRef.current.scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const swipeRight = () => {
    if (cardsWrapperRef.current) {
      const nextCardPosition =
        cardWidth <= 0
          ? cardWidth
          : Math.ceil(cardsWrapperRef.current.scrollLeft / cardWidth) *
            cardWidth;
      cardsWrapperRef.current.scrollBy({
        left: nextCardPosition - cardsWrapperRef.current.scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (cardsWrapperRef.current) {
      cardsWrapperRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (cardsWrapperRef.current) {
      cardsWrapperRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const wrapper = cardsWrapperRef.current;
    if (!wrapper) return;
    /// avoid scroll bouncing on Ios
    const clampScroll = () => {
      if (wrapper.scrollLeft < 0) wrapper.scrollLeft = 0;
      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
      if (wrapper.scrollLeft > maxScroll) wrapper.scrollLeft = maxScroll;
    };

    wrapper.addEventListener('scroll', clampScroll);
    return () => wrapper.removeEventListener('scroll', clampScroll);
  }, []);

  return (
    <Container id={sectionId} {...handlers}>
      <TitleContainer>
        <Title className="title-large">{headerTitle}</Title>
        <ChevronWrapper>
          <IconChevronLeft
            data-testid="chevron-left"
            id="chevron-left"
            color="gray12"
            onClick={scrollLeft}
            disabled={isLeftDisabled}
            size={48}
          />
          <IconChevronRight
            data-testid="chevron-right"
            id="chevron-right"
            color="gray12"
            onClick={scrollRight}
            disabled={isRightDisabled}
            size={48}
          />
        </ChevronWrapper>
      </TitleContainer>

      <CardsWrapper ref={cardsWrapperRef}>
        <LeftFiller />
        {cards.map((card, index) => (
          <PlanCard
            key={card.title}
            {...card}
            ref={index === 0 ? cardRef : null}
          />
        ))}
        <RightFiller />
      </CardsWrapper>
    </Container>
  );
}

export default HowYourPlanWorks;
